import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { TimePeriod } from "./TimePeriodSelector";
import { getDayXAxis, getXAxisTickFormatterForPeriod } from "./axis";
import type { BloodGlucoseReading } from "@/types/tracker";
import {
  CHART_COLORS,
  CHART_CONFIG,
  BLOOD_GLUCOSE_COLORS,
} from "./chart-theme";

interface BloodGlucoseChartProps {
  data: BloodGlucoseReading[];
  selectedPeriod: TimePeriod;
  currentDate: Date;
  yAxisConfig: { min: number; max: number };
  selectedFilters: string[];
}

export const BloodGlucoseChart = ({
  data,
  selectedPeriod,
  currentDate,
  yAxisConfig,
  selectedFilters,
}: BloodGlucoseChartProps) => {
  const hasData = data && data.length > 0;

  // Calculate Y-axis interval based on range
  const calculateYAxisInterval = (min: number, max: number): number => {
    const range = max - min;
    if (range <= 10) return 2;
    if (range <= 20) return 5;
    if (range <= 50) return 10;
    if (range <= 100) return 20;
    if (range <= 200) return 50;
    if (range <= 500) return 100;
    return 200;
  };

  // Get Y-axis ticks based on calculated interval
  const getYAxisTicks = (min: number, max: number): number[] => {
    const interval = calculateYAxisInterval(min, max);
    const ticks: number[] = [];
    let tick = Math.ceil(min);
    while (tick <= Math.floor(max)) {
      ticks.push(tick);
      tick += interval;
    }
    const last = Math.floor(max);
    if (ticks.length === 0 || ticks[ticks.length - 1] !== last) {
      ticks.push(last);
    }
    return ticks;
  };

  // Filter data based on selected filters
  const getFilteredData = () => {
    if (selectedFilters.includes("All")) {
      return data;
    }
    return data.filter((reading) =>
      selectedFilters.includes(reading.sample_type.name)
    );
  };

  // Create empty chart data structure for week/month/year views when no data
  const createEmptyChartData = () => {
    if (selectedPeriod === "day") return [];

    let allCategories: string[] = [];

    if (selectedPeriod === "week") {
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();
      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    return allCategories.map((key) => ({
      categoryKey: key,
      ...Object.fromEntries(
        Object.keys(BLOOD_GLUCOSE_COLORS).map((sampleType) => [
          sampleType,
          null,
        ])
      ),
    }));
  };

  // Transform data for chart display
  const transformDataForChart = () => {
    const filteredData = getFilteredData();

    const transformedData = filteredData
      .map((reading) => {
        let fullDate;
        try {
          if (reading.time.includes("AM") || reading.time.includes("PM")) {
            fullDate = new Date(`${reading.date} ${reading.time}`);
          } else {
            fullDate = new Date(`${reading.date}T${reading.time}`);
          }
          if (isNaN(fullDate.getTime())) {
            fullDate = new Date(reading.date);
          }
        } catch (error) {
          fullDate = new Date(reading.date);
        }

        let formattedLabel;
        let categoryKey;

        switch (selectedPeriod) {
          case "day":
            formattedLabel = fullDate.toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            });
            categoryKey = formattedLabel;
            break;
          case "week":
            formattedLabel = fullDate.toLocaleDateString("en-US", {
              weekday: "short",
            });
            categoryKey = formattedLabel;
            break;
          case "month":
            formattedLabel = fullDate.getDate().toString();
            categoryKey = formattedLabel;
            break;
          case "year":
            formattedLabel = fullDate.toLocaleDateString("en-US", {
              month: "short",
            });
            categoryKey = formattedLabel;
            break;
          default:
            formattedLabel = reading.time;
            categoryKey = formattedLabel;
        }

        return {
          time: reading.time,
          value: reading.value,
          label: formattedLabel,
          categoryKey: categoryKey,
          sampleType: reading.sample_type.name,
          date: reading.date,
          fullDate: fullDate,
          xValue: fullDate.getTime(),
        };
      })
      .sort((a, b) => a.fullDate.getTime() - b.fullDate.getTime());

    return transformedData;
  };

  // Create unified chart data for week/month/year views with complete range
  const createUnifiedChartData = () => {
    if (selectedPeriod === "day") return [];

    let allCategories: string[] = [];

    if (selectedPeriod === "week") {
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();
      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    const chartData = transformDataForChart();

    const unifiedData = allCategories.map((category) => {
      const dataPoint: any = { categoryKey: category };

      Object.keys(BLOOD_GLUCOSE_COLORS).forEach((sampleType) => {
        const sampleData = chartData.find(
          (item) =>
            item.categoryKey === category && item.sampleType === sampleType
        );
        dataPoint[sampleType] = sampleData ? sampleData.value : null;
      });

      return dataPoint;
    });

    return unifiedData;
  };

  const chartData = hasData ? transformDataForChart() : [];

  if (selectedPeriod === "day") {
    const dayXAxis = getDayXAxis(currentDate);
    const scatterData = hasData
      ? chartData
      : dayXAxis.ticks.map((tick: number) => ({
          xValue: tick,
          value: null,
        }));

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <ScatterChart data={scatterData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            type="number"
            dataKey="xValue"
            scale="time"
            domain={dayXAxis.domain}
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            ticks={dayXAxis.ticks}
            tickFormatter={dayXAxis.tickFormatter}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[yAxisConfig.min, yAxisConfig.max]}
            ticks={getYAxisTicks(yAxisConfig.min, yAxisConfig.max)}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                    <p className="text-sm font-medium text-muted-foreground">
                      {data.sampleType || "Blood Glucose"}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {data.value} mg/dL
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.time}
                    </p>
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 2 }}
          />
          {hasData && (
            <Scatter
              dataKey="value"
              shape={(props: any) => {
                const { cx, cy, payload } = props;
                if (payload) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={CHART_CONFIG.dotRadius + 1}
                      fill={
                        BLOOD_GLUCOSE_COLORS[payload.sampleType] ||
                        CHART_COLORS.bloodGlucose
                      }
                      strokeWidth={0}
                    />
                  );
                }
                return null;
              }}
            />
          )}
        </ScatterChart>
      </ChartContainer>
    );
  } else {
    const weekMonthYearData = hasData
      ? createUnifiedChartData()
      : createEmptyChartData();

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <LineChart data={weekMonthYearData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            dataKey="categoryKey"
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            interval={0}
            tickFormatter={getXAxisTickFormatterForPeriod(
              selectedPeriod,
              currentDate
            )}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[yAxisConfig.min, yAxisConfig.max]}
            ticks={getYAxisTicks(yAxisConfig.min, yAxisConfig.max)}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const activeDot = payload.find((p) => p.value !== null);
                if (activeDot) {
                  return (
                    <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                      <p className="text-sm text-muted-foreground">
                        {activeDot.dataKey}
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {activeDot.value} mg/dL
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activeDot.payload.categoryKey}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
          />
          {hasData &&
            Object.entries(BLOOD_GLUCOSE_COLORS).map(([sampleType, color]) => (
              <Line
                key={sampleType}
                type="monotone"
                dataKey={sampleType}
                stroke={color}
                strokeWidth={CHART_CONFIG.strokeWidth}
                dot={{
                  fill: color,
                  strokeWidth: 0,
                  r: CHART_CONFIG.dotRadius,
                }}
                activeDot={{
                  r: CHART_CONFIG.activeDotRadius,
                  strokeWidth: 0,
                  fill: color,
                }}
                connectNulls={false}
              />
            ))}
        </LineChart>
      </ChartContainer>
    );
  }
};

export { BLOOD_GLUCOSE_COLORS };
